import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, X, Save } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "employee" });

  const reload = () => api.get("/users").then((r) => setRows(r.data));

  useEffect(() => {
    reload();
  }, []);

  const submit = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setCreating(false);
      setForm({ email: "", name: "", password: "", role: "employee" });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`);
    reload();
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Admin · Users"
        title="User management"
        subtitle="Create employees, dealers and administrators."
        actions={
          <button
            onClick={() => setCreating(true)}
            className="btn-primary"
            data-testid="add-user-btn"
          >
            <Plus size={16} /> New user
          </button>
        }
      />

      <div className="p-8">
        <div className="panel">
          <table className="w-full grid-lines">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Created</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`user-row-${r.email}`}>
                  <td className="font-semibold">{r.name}</td>
                  <td className="mono">{r.email}</td>
                  <td>
                    <span className="label-tech">{r.role}</span>
                  </td>
                  <td className="text-xs text-ink-muted">
                    {new Date(r.created_at).toLocaleDateString("en-IN")}
                  </td>
                  <td>
                    {r.id !== user.id && (
                      <button
                        onClick={() => del(r.id)}
                        className="btn-ghost text-brand-danger"
                        data-testid={`del-user-${r.email}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-ink/60 flex items-center justify-center p-4 z-50">
          <div className="panel-heavy bg-white w-full max-w-md" data-testid="create-user-dialog">
            <div className="px-5 py-4 border-b-2 border-ink flex items-center justify-between">
              <div className="display font-black uppercase text-xl tracking-tighter">
                New user
              </div>
              <button className="btn-ghost" onClick={() => setCreating(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="label-tech mb-1 block">Name</label>
                <input
                  className="input-tech"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="new-user-name"
                />
              </div>
              <div>
                <label className="label-tech mb-1 block">Email</label>
                <input
                  type="email"
                  className="input-tech"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="new-user-email"
                />
              </div>
              <div>
                <label className="label-tech mb-1 block">Password</label>
                <input
                  type="password"
                  className="input-tech"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="new-user-password"
                />
              </div>
              <div>
                <label className="label-tech mb-1 block">Role</label>
                <select
                  className="input-tech"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="new-user-role"
                >
                  <option value="admin">admin</option>
                  <option value="employee">employee</option>
                  <option value="dealer">dealer</option>
                </select>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={submit} data-testid="create-user-submit">
                <Save size={16} /> Create
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
